/**
 * Endpoints de **Logística → Bóvedas** (`/api/logistica/bovedas`).
 *
 * El listado va paginado en el servidor con los filtros del panel anterior
 * (fechas, usuario, monto mínimo) más el buscador libre, y la edición y la
 * inhabilitación se piden por bóveda.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '@/lib/http/api-client';

import type { BovedaEditable, BovedasFiltros, BovedasRespuesta } from './bovedas.types';

export const bovedasKeys = {
  lista: (filtros: BovedasFiltros) => ['logistica', 'bovedas', filtros] as const,
};

/** Arma la cadena de consulta del listado. */
function cadenaDe(filtros: BovedasFiltros): string {
  const parametros = new URLSearchParams();

  if (filtros.desde) parametros.set('desde', filtros.desde);
  if (filtros.hasta) parametros.set('hasta', filtros.hasta);
  if (filtros.usuario) parametros.set('usuario', filtros.usuario);
  if (filtros.montoMin) parametros.set('monto_min', filtros.montoMin);
  if (filtros.search) parametros.set('search', filtros.search);
  if (filtros.orden) parametros.set('orden', filtros.orden);
  if (filtros.direccion) parametros.set('direccion', filtros.direccion);
  if (filtros.page) parametros.set('page', String(filtros.page));
  if (filtros.perPage) parametros.set('per_page', String(filtros.perPage));

  const cadena = parametros.toString();

  return cadena ? `?${cadena}` : '';
}

export const bovedasApi = {
  listar: (filtros: BovedasFiltros = {}, signal?: AbortSignal): Promise<BovedasRespuesta> =>
    apiClient.get<BovedasRespuesta>(`/api/logistica/bovedas${cadenaDe(filtros)}`, { signal }),

  editar: (id: number, boveda: BovedaEditable): Promise<{ message: string }> =>
    apiClient.put<{ message: string }>(`/api/logistica/bovedas/${id}`, {
      body: {
        total: Number(boveda.total),
        fecha: boveda.fecha,
        observacion: boveda.observacion,
      },
    }),

  eliminar: (id: number): Promise<{ message: string; id: number }> =>
    apiClient.delete<{ message: string; id: number }>(`/api/logistica/bovedas/${id}`),
};

/** Bóvedas del filtro pedido. */
export function useBovedas(filtros: BovedasFiltros = {}) {
  return useQuery({
    queryKey: bovedasKeys.lista(filtros),
    queryFn: ({ signal }) => bovedasApi.listar(filtros, signal),
  });
}

/** Guarda los cambios de una bóveda y refresca el listado. */
export function useEditarBoveda() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: ({ id, boveda }: { id: number; boveda: BovedaEditable }) =>
      bovedasApi.editar(id, boveda),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: ['logistica', 'bovedas'] });
    },
  });
}

/** Inhabilita una bóveda y refresca el listado. */
export function useEliminarBoveda() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => bovedasApi.eliminar(id),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: ['logistica', 'bovedas'] });
    },
  });
}
